/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { AlertTriangle, CheckCircle2, FileText, ReceiptText, XCircle, Bell } from 'lucide-react';
import { type LucideIcon } from 'lucide-react';
import { useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/button.js';
import { PageHeader } from '../components/ui/page-header.js';
import { QueryState } from '../components/ui/query-state.js';
import { type Notification } from '../lib/api/notifications.js';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../lib/queries/notifications.js';
import { formatIst } from '../lib/utils/datetime.js';

type Tab = 'all' | 'unread';

function iconFor(type: string): { Icon: LucideIcon; tone: string } {
  if (type === 'proforma_approved' || type === 'consultant_invoice_approved') {
    return { Icon: CheckCircle2, tone: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10' };
  }
  if (type === 'proforma_declined' || type === 'consultant_invoice_rejected') {
    return { Icon: XCircle, tone: 'text-rose-600 bg-rose-50 dark:bg-rose-500/10' };
  }
  if (type === 'consultant_invoice_submitted' || type === 'consultant_invoice_paid') {
    return { Icon: ReceiptText, tone: 'text-sky-600 bg-sky-50 dark:bg-sky-500/10' };
  }
  if (type === 'therapist_document_uploaded') {
    return { Icon: FileText, tone: 'text-[var(--accent)] bg-slate-100 dark:bg-slate-800' };
  }
  if (type === 'followup_raised') {
    return { Icon: AlertTriangle, tone: 'text-amber-600 bg-amber-50 dark:bg-amber-500/10' };
  }
  return { Icon: Bell, tone: 'text-[var(--accent)] bg-slate-100 dark:bg-slate-800' };
}

function NotificationItem({ item }: { readonly item: Notification }): JSX.Element {
  const mark = useMarkNotificationRead();
  const { Icon, tone } = iconFor(item.type);
  const onClick = useCallback(() => {
    if (item.readAt === null) {
      mark.mutate(item.id);
    }
  }, [mark, item.id, item.readAt]);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`focus-ring flex w-full items-start gap-3 rounded-xl border border-slate-200 p-4 text-left dark:border-slate-700 ${
        item.readAt === null ? 'bg-[var(--accent)]/5' : 'bg-white dark:bg-slate-900'
      }`}
    >
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${tone}`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="font-medium text-slate-800 dark:text-white">{item.title}</span>
          {item.readAt === null && <span className="h-2 w-2 rounded-full bg-[var(--accent)]" />}
        </span>
        {item.body !== null && (
          <span className="mt-0.5 block text-sm text-slate-500 dark:text-slate-400">
            {item.body}
          </span>
        )}
        <span className="mt-1 block text-xs text-slate-400">
          {formatIst(item.createdAt, 'd MMM, h:mm a')}
        </span>
      </span>
    </button>
  );
}

/** Inbox / notifications page (S13), shared by every portal. */
export function InboxPage(): JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('all');
  const query = useNotifications();
  const markAll = useMarkAllNotificationsRead();
  const all = query.data ?? [];
  const items = tab === 'unread' ? all.filter((n) => n.readAt === null) : all;
  const onAll = useCallback(() => {
    setTab('all');
  }, []);
  const onUnread = useCallback(() => {
    setTab('unread');
  }, []);
  const onMarkAll = useCallback(() => {
    markAll.mutate();
  }, [markAll]);

  return (
    <div className="p-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <PageHeader title={t('inbox.title')} subtitle={t('inbox.subtitle')} />
        <Button type="button" variant="outline" onClick={onMarkAll} disabled={markAll.isPending}>
          {t('inbox.markAllRead')}
        </Button>
      </div>

      <div className="mb-4 flex gap-2" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'all'}
          onClick={onAll}
          className={`focus-ring rounded-full px-4 py-1.5 text-sm font-medium ${tab === 'all' ? 'bg-[var(--accent)] text-white' : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'}`}
        >
          {t('inbox.all')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'unread'}
          onClick={onUnread}
          className={`focus-ring rounded-full px-4 py-1.5 text-sm font-medium ${tab === 'unread' ? 'bg-[var(--accent)] text-white' : 'border border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'}`}
        >
          {t('inbox.unread')}
        </button>
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={items.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('inbox.empty')}
      >
        <div className="space-y-2">
          {items.map((item) => (
            <NotificationItem key={item.id} item={item} />
          ))}
        </div>
      </QueryState>
    </div>
  );
}
